import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';

interface FormData {
  parentName: string;
  childAge: string;
  phone: string;
  message: string;
}

const Contact: React.FC = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const [formData, setFormData] = useState<FormData>({
    parentName: '',
    childAge: '',
    phone: '',
    message: '',
  });

  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success'>('idle');

  const ageOptions = [
    { value: '', label: 'Select Age' },
    { value: '18 months -2.5 years', label: '18 months - 2.5 years' },
    { value: '2.5 - 3.5 years', label: '2.5 - 3.5 years' },
    { value: '3.5 - 4.5 years', label: '3.5 - 4.5 years' },
    { value: '4.5 - 5.5 years', label: '4.5 - 5.5 years' },
  ];

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.parentName || !formData.phone) {
      alert('Please fill in all required fields');
      return;
    }

    // Build pre-filled Google Form URL
    const baseUrl = 'https://docs.google.com/forms/d/e/1FAIpQLSdxnZntDfb9TEFuln0eFuEsSN56CJNYUpUGqjnaiQ1Qwn9Fgg/viewform';
    const params = new URLSearchParams({
      'entry.2143641614': formData.parentName,
      'entry.242412572': formData.phone,
      'entry.722177932': formData.childAge,
      'entry.1729255873': formData.message,
      'usp': 'pp_url'
    });

    // Open Google Form in new tab with pre-filled data
    window.open(`${baseUrl}?${params.toString()}`, '_blank');

    // Show success message and clear form
    setSubmitStatus('success');
    setFormData({
      parentName: '',
      childAge: '',
      phone: '',
      message: '',
    });

    // Reset success message after 5 seconds
    setTimeout(() => {
      setSubmitStatus('idle');
    }, 5000);
  };

  return (
    <section id="contact" className="p-16 bg-brand-light">
      <div className="container">
        <motion.div
          ref={ref}
          className="section-header"
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
        >
          <div className='text-3xl font-bold text-brand-pink mb-8'>Get in Touch</div>
          <div className='text-lg text-brand-purple mb-8'>Ready to give your child the best start? Contact us today</div>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-12">
          {/* Contact Information */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="bg-brand-yellow bg-opacity-10 border-l-8 border-brand-yellow rounded-2xl p-8 shadow-bubble"
          >
            <h3 className="text-2xl font-bold text-brand-yellow mb-6">Visit Us</h3>
            
            <div className="space-y-6 text-brand-dark">
              <div>
                <h4 className="text-lg font-semibold text-brand-pink mb-2">Address</h4>
                <p>
                  B-10, SIRSA-125055<br />
                  (HARYANA)
                </p>
              </div>
              
              <div>
                <h4 className="text-lg font-semibold text-brand-pink mb-2">Phone</h4>
                <p>+91 7494901888</p>
              </div>
              
              <div>
                <h4 className="text-lg font-semibold text-brand-pink mb-2">Email</h4>
                <p>neev.sps@gmail.com</p>
              </div>
              
              <div>
                <h4 className="text-lg font-semibold text-brand-pink mb-2">School Hours</h4>
                <p>
                  Monday - Friday: 8:00 AM - 6:00 PM<br />
                  Saturday: 9:00 AM - 1:00 PM
                </p>
              </div>
            </div>
          </motion.div>

          {/* Contact Form */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="bg-brand-blue bg-opacity-10 border-l-8 border-brand-blue rounded-2xl p-8 shadow-bubble"
          >
            <form onSubmit={handleSubmit} className="space-y-6">
              <h3 className="text-2xl font-bold text-brand-blue mb-6">Send us a Message</h3>
              
              <div className="form-group">
                <label htmlFor="parentName" className="form-label">Parent Name *</label>
                <input
                  type="text"
                  id="parentName"
                  name="parentName"
                  value={formData.parentName}
                  onChange={handleInputChange}
                  className="form-control"
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="childAge" className="form-label">Child Age</label>
                <select
                  id="childAge"
                  name="childAge"
                  value={formData.childAge}
                  onChange={handleInputChange}
                  className="form-control"
                >
                  {ageOptions.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="phone" className="form-label">Phone *</label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  className="form-control"
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="message" className="form-label">Message</label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  rows={4}
                  className="form-control placeholder:text-black"
                  placeholder="Tell us about your child or any questions you have..."
                />
              </div>

              {/* Status Messages */}
              {submitStatus === 'success' && (
                <motion.div
                  className="bg-green-100 border border-green-300 text-green-800 p-4 rounded-lg"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                >
                  <strong>Form Opened!</strong><br />
                  Please click "Submit" in the Google Form tab to complete your submission. Thank you for your interest in Neev Play School!
                </motion.div>
              )}


              <motion.button
                type="submit"
                className="w-full bg-brand-pink text-brand-white border-2 border-brand-pink rounded-bubble px-10 py-4 font-bold text-lg shadow-bubble hover:bg-brand-yellow hover:text-brand-dark hover:border-brand-yellow transition-all duration-300 transform hover:scale-105"
                whileHover={{ y: -2 }}
                whileTap={{ scale: 0.95 }}
              >
                Send Message
              </motion.button>
            </form>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Contact; 